/**
 * PRNG & Cryptographic Seed Utilities for Kazakan Cognitive Test
 * Pure client-side, deterministic reproducible pseudo-random generator
 */

export function createSessionSeed() {
  const timeComponent = (typeof performance !== 'undefined' && performance.now) 
    ? Math.floor(performance.now() * 1000) 
    : Date.now();
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const buf = new Uint32Array(4);
    crypto.getRandomValues(buf);
    return ((buf[0] ^ (buf[1] << 11)) ^ ((buf[2] << 21) ^ (buf[3] >>> 7)) ^ (timeComponent & 0xFFFFFFFF)) >>> 0;
  }
  return (((Date.now() ^ Math.floor(Math.random() * 0xFFFFFFFF)) + timeComponent) >>> 0);
}

/**
 * Mulberry32 32-bit PRNG
 * @param {number} seed 32-bit unsigned integer
 */
export function createPRNG(seed) {
  let s = (seed >>> 0) || 1337;

  return {
    /** Returns float in [0, 1) */
    next() {
      s |= 0;
      s = (s + 0x6D2B79F5) | 0;
      let t = Math.imul(s ^ (s >>> 15), 1 | s);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    },

    /** Returns integer in [min, max] inclusive */
    int(min, max) {
      return Math.floor(this.next() * (max - min + 1)) + min;
    },

    /** Picks one element randomly from array */
    pick(array) {
      if (!array || array.length === 0) return null;
      const idx = Math.floor(this.next() * array.length);
      return array[idx];
    },

    /** Shuffles an array in place (Fisher-Yates) */
    shuffle(array) {
      const arr = [...array];
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(this.next() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    },

    /** Sample k distinct items from array */
    sample(array, k) {
      const shuffled = this.shuffle(array);
      return shuffled.slice(0, Math.min(k, array.length));
    }
  };
}
